import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import * as keyLicensesService from '../services/keyLicenses';
import * as featuresService from '../services/features';
import * as permanentLicensesService from '../services/permanentLicenses';
import * as offlineLicensesService from '../services/offlineLicenses';
import * as accountsService from '../services/accounts';
import * as webViewerServersService from '../services/webViewerServers';
import * as webViewerLicensesService from '../services/webViewerLicenses';

export const objectDataNames = {
  accountsProviders: 'accountsProviders',
  products: 'products',
  features: 'features',
  licenseKeyTypes: 'licenseKeyTypes',
  onlineLicenseLivingStates: 'onlineLicenseLivingStates',
  offlineLicenseLivingStates: 'offlineLicenseLivingStates',
  onlineLicenseProducts: 'onlineLicenseProducts',
  offlineLicenseProducts: 'offlineLicenseProducts',
  dicomConnectionLimitationTypes: 'dicomConnectionLimitationTypes',
  featureTypes: 'featureTypes',
  webViewerServerLivingStates: 'webViewerServerLivingStates',
  webViewerLicenseLivingStates: 'webViewerLicenseLivingStates',
}

const objectDataMapping = {
  [objectDataNames.licenseKeyTypes]: {
    getData: () => keyLicensesService.getLicenseKeyTypes(),
  },
  [objectDataNames.features]: {
    getData: (field) => featuresService.getProductFeatures(field),
  },
  [objectDataNames.onlineLicenseLivingStates]: {
    getData: () => permanentLicensesService.getOnlineLicLivingStates(),
  },
  [objectDataNames.offlineLicenseLivingStates]: {
    getData: () => offlineLicensesService.getOfflineLicLivingStates(),
  },
  [objectDataNames.onlineLicenseProducts]: {
    getData: () => keyLicensesService.getOnlinelicenseProducts(),
  },
  [objectDataNames.offlineLicenseProducts]: {
    getData: () => keyLicensesService.getOfflinelicenseProducts(),
  },
  [objectDataNames.products]: {
    getData: () => keyLicensesService.getProducts(),
  },
  [objectDataNames.accountsProviders]: {
    getData: () => accountsService.getProviders(),
  },
  [objectDataNames.dicomConnectionLimitationTypes]: {
    getData: () => offlineLicensesService.getDicomLimitTypes(),
  },
  [objectDataNames.featureTypes]: {
    getData: () => featuresService.getFeatureTypes(),
  },
  [objectDataNames.webViewerServerLivingStates]: {
    getData: () => webViewerServersService.getWebViewerServerLivingStates(),
  },
  [objectDataNames.webViewerLicenseLivingStates]: {
    getData: () => webViewerLicensesService.getWebViewerLicenseLivingStates(),
  },
};

const sliceName = 'objectData';

const initialState = Object.keys(objectDataMapping).reduce((acc, i) => ({
  ...acc,
  [i]: {
    data: [],
    isFetching: false,
    error: null,
  },
}), {});

export const fetchObjectData = createAsyncThunk(
  `${sliceName}/fetch`,
  (args, thunkAPI) => {
    const { objectName, field } = args;
    return objectDataMapping[objectName]
      .getData(field)
      .catch((error) => {
        error.clientMessage = `Can't get ${objectName} history`;
        return thunkAPI.rejectWithValue(error)
      });
  }
)

const objectData = createSlice({
  name: sliceName,
  initialState,
  reducers: {},
  extraReducers: {
    [fetchObjectData.fulfilled]: (state, action) => {
      const objectData = action.payload;
      const { objectName } = action.meta.arg;
      state[objectName].data = objectData;
      state[objectName].isFetching = false;
    },
    [fetchObjectData.pending]: (state, action) => {
      const { objectName } = action.meta.arg;
      state[objectName].isFetching = true;
      state[objectName].error = null;
    },
    [fetchObjectData.rejected]: (state, action) => {
      const { objectName } = action.meta.arg;
      state[objectName].isFetching = false;
      state[objectName].error = action.payload;
    }
  }
});

const { reducer } = objectData;

export const objectDataSelectors = {
  providersState: (state) => state[sliceName][objectDataNames.accountsProviders],
  productsState: (state) => state[sliceName][objectDataNames.products],
  featuresState: (state) => state[sliceName][objectDataNames.features],
  licenseKeyTypesState: (state) => state[sliceName][objectDataNames.licenseKeyTypes],
  onlineLicenseLivingStatesState: (state) => state[sliceName][objectDataNames.onlineLicenseLivingStates],
  offlineLicenseLivingStatesState: (state) => state[sliceName][objectDataNames.offlineLicenseLivingStates],
  onlineLicenseProductsState: (state) => state[sliceName][objectDataNames.onlineLicenseProducts],
  offlineLicenseProductsState: (state) => state[sliceName][objectDataNames.offlineLicenseProducts],
  dicomConnectionLimitationTypesState: (state) => state[sliceName][objectDataNames.dicomConnectionLimitationTypes],
  featureTypesState: (state) => state[sliceName][objectDataNames.featureTypes],
  webViewerServerLivingState: (state) => state[sliceName][objectDataNames.webViewerServerLivingStates],
  webViewerLicenseLivingState: (state) => state[sliceName][objectDataNames.webViewerLicenseLivingStates],
}

export default reducer;



// WEBPACK FOOTER //
// ./src/store/objectDataSlice.js